import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBag } from '../context/BagContext';
import { useProducts } from '../context/ProductsContext';
import { categoryService, cartService, productService } from '../services/api';
import {
  CART_QTY_MAX,
  findCatalogProduct,
  purchasableMax,
  resolveAvailableStockFromProduct,
} from '../utils/cartLineStock';
import ConfirmationModal from './ConfirmationModal';
import {
  getCategorySlug as getCategorySlugUtil,
  sortMainCategoriesForDisplay,
} from '../utils/categoryUtils';
import useSiteBranding from '../hooks/useSiteBranding';
import {
  parseContactChannelsFromStorage,
  CONTACT_CHANNELS_UPDATED_EVENT,
  computeShippingFromSubtotal,
} from '../utils/whatsappContact';
import { mergeGuestCartToApi, guestBagReactSnapshotRef } from '../utils/mergeGuestCartToApi';
import { usePushNotifications } from '../context/PushNotificationContext';
import ThemeModeIconButton from './ThemeModeIconButton';
import UserNotificationsModal from './UserNotificationsModal';
import './Header.css';

/** Viewport width at which hamburger + left drawer (Search, My Account, …) are used */
const MOBILE_NAV_MAX_PX = 1024;

/**
 * Accent theme for mobile category drill drawer (slug + name heuristics).
 * @param {(cat: object) => string} slugResolver — e.g. Header's getCategorySlug bound with allCategories
 */
function getMobileNavDrillThemeKey(category, slugResolver) {
  if (!category || typeof slugResolver !== 'function') return 'default';
  const slug = String(slugResolver(category) || '').toLowerCase();
  const name = String(category.name || '')
    .toLowerCase()
    .replace(/['']/g, '');
  const hay = `${slug} ${name}`;
  const isWomen = hay.includes('women') || hay.includes('ladies') || hay.includes('woman');
  const isMen =
    !isWomen &&
    (hay.includes("men's") ||
      hay.includes('mens') ||
      /\bmen\b/.test(hay) ||
      hay.includes('male'));
  if (isWomen) return 'women';
  if (isMen) return 'men';
  if (
    hay.includes('kid') ||
    hay.includes('child') ||
    hay.includes('baby') ||
    hay.includes('infant')
  ) {
    return 'kids';
  }
  if (hay.includes('accessor')) return 'accessories';
  if (hay.includes('fragran') || hay.includes('perfume') || hay.includes('scent')) return 'fragrances';
  if (hay.includes('shoe') || hay.includes('footwear') || hay.includes('sandal')) return 'shoes';
  return 'default';
}

/** Header guest account — solid gold user mark (no text label; works in WebView) */
function MobileGuestUserIcon() {
  return (
    <svg
      className="mobile-user-icon-gold"
      viewBox="0 0 24 24"
      aria-hidden
      focusable="false"
    >
      <circle className="mobile-user-icon-gold__head" cx="12" cy="8.75" r="3.75" />
      <path
        className="mobile-user-icon-gold__body"
        d="M4.75 20.5c.55-3.85 3.45-6.5 7.25-6.5s6.7 2.65 7.25 6.5"
      />
    </svg>
  );
}

const Header = () => {
  const { siteName, siteLogoUrl } = useSiteBranding();

  const renderSiteBrandLink = (className, { onNavigate } = {}) => (
    <Link
      to="/"
      className={className}
      aria-label={`${siteName || 'Home'} — Home`}
      onClick={() => {
        if (onNavigate) onNavigate();
      }}
    >
      {siteLogoUrl ? (
        <span className="logo-brand-lockup">
          <img src={siteLogoUrl} alt="" className="site-logo-image" />
          <span className="logo-brand-name">{siteName}</span>
        </span>
      ) : (
        <span className="logo-brand-name logo-brand-name--solo">{siteName}</span>
      )}
    </Link>
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isMobileAccountMenuOpen, setIsMobileAccountMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  /** Mobile only: hide top bar on scroll down, show on scroll up / at top */
  const [headerBarHiddenMobile, setHeaderBarHiddenMobile] = useState(false);
  const lastScrollYRef = useRef(0);
  const mobileSearchInputRef = useRef(null);
  const [useMobileSearchOverlay, setUseMobileSearchOverlay] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_NAV_MAX_PX
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { recentNotifications, unreadCount } = usePushNotifications();
  const {
    bagItems: guestBagItems,
    bagTotal: guestBagTotal,
    updateQuantity: updateGuestQuantity,
    removeFromBag: removeGuestItem,
    clearBag: clearGuestBag,
  } = useBag();
  const { products: catalogProducts } = useProducts();
  const [cartLineStock, setCartLineStock] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // Store all categories including subcategories
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set()); // Track expanded subcategories on mobile
  const [expandedChildren, setExpandedChildren] = useState(new Set()); // Track expanded children categories on mobile
  /** Mobile drawer: multi-step category drill — main subs, then siblings + children */
  const [mobileCategoryPanel, setMobileCategoryPanel] = useState(null);

  // State for authenticated user's cart
  const [cart, setCart] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const fetchingCartRef = useRef(false);
  
  // Fetch cart function
  const fetchCart = async () => {
    if (!isAuthenticated || fetchingCartRef.current) return;
    
    fetchingCartRef.current = true;
    setCartLoading(true);
    try {
      await mergeGuestCartToApi(clearGuestBag, () => guestBagItems);
      const response = await cartService.getCart();
      if (response?.data) {
        setCart(response.data);
      }
    } catch (error) {
      console.error('Error fetching cart in Header:', error);
    } finally {
      setCartLoading(false);
      fetchingCartRef.current = false;
    }
  };

  // Fetch cart for authenticated users on mount and auth change
  useEffect(() => {
    if (!isAuthenticated) {
      setCart(null);
      fetchingCartRef.current = false;
      return;
    }
    
    fetchCart();
  }, [isAuthenticated]);

  // Listen for cart update events (when items are added/removed)
  useEffect(() => {
    const handleCartUpdate = () => {
      if (isAuthenticated) {
        // Small delay to ensure API has processed the update
        setTimeout(() => {
          fetchCart();
        }, 300);
      }
    };

    // Listen for custom cart update events
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('itemAddedToCart', handleCartUpdate);
    window.addEventListener('itemRemovedFromCart', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('itemAddedToCart', handleCartUpdate);
      window.removeEventListener('itemRemovedFromCart', handleCartUpdate);
    };
  }, [isAuthenticated]);

  // Refresh server cart when slide-out bag opens (logged-in users)
  useEffect(() => {
    if (!isCartOpen || !isAuthenticated) return;
    fetchCart();
  }, [isCartOpen, isAuthenticated]);

  // Helper function to extract image URL
  const getImageUrl = (imageData) => {
    if (!imageData) return '';
    if (typeof imageData === 'string') return imageData;
    if (Array.isArray(imageData) && imageData.length > 0) {
      return typeof imageData[0] === 'string' ? imageData[0] : imageData[0].url || '';
    }
    if (imageData.url) return imageData.url;
    return '';
  };
  
  // Determine which items to show - authenticated users get API cart, guests get local storage
  const bagItems = useMemo(() => {
    if (isAuthenticated && cart?.items) {
      return cart.items.map((item) => {
        const productImage = item.product?.images || item.product?.image;
        const imageUrl = getImageUrl(productImage);

        return {
          itemId: item._id,
          id: item.product._id || item.product.id,
          name: item.product.name,
          price: item.price,
          originalPrice: item.originalPrice || item.product.originalPrice,
          image: imageUrl,
          selectedSize: item.size,
          selectedColor: item.color,
          quantity: item.quantity,
          category: item.product.category,
          subcategory: item.product.subcategory,
          total: item.total,
        };
      });
    }
    return guestBagItems || [];
  }, [isAuthenticated, cart, guestBagItems]);
  
  const bagTotal = isAuthenticated && cart ? cart.subtotal : guestBagTotal;
  // Calculate bag count as total quantity, not number of items
  const bagCount = bagItems.reduce((total, item) => total + (item.quantity || 1), 0);

  const resolveLineStock = useCallback(
    async (item) => {
      const productId = item.id;
      const color = item.selectedColor ?? 'default';
      const size = item.selectedSize ?? 'default';
      const lineKey = item.itemId;

      let embeddedProduct = null;
      if (isAuthenticated && cart?.items) {
        const row = cart.items.find((r) => String(r._id) === String(lineKey));
        embeddedProduct = row?.product ?? null;
      }

      const catalog = findCatalogProduct(catalogProducts, productId);
      const fromCatalog = resolveAvailableStockFromProduct(catalog, color, size);
      if (fromCatalog != null) return { lineKey, stock: fromCatalog };

      const fromEmbedded = resolveAvailableStockFromProduct(embeddedProduct, color, size);
      if (fromEmbedded != null) return { lineKey, stock: fromEmbedded };

      try {
        const sizeParam = size === 'default' ? '' : size;
        const res = await productService.checkStockAvailability(productId, color, sizeParam);
        const raw = res?.data?.stock ?? res?.data?.quantity ?? res?.stock;
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n)) return { lineKey, stock: Math.max(0, n) };
      } catch {
        /* fall through */
      }

      return { lineKey, stock: CART_QTY_MAX };
    },
    [catalogProducts, cart, isAuthenticated]
  );

  useEffect(() => {
    if (!isCartOpen || !bagItems.length) {
      setCartLineStock({});
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      const entries = await Promise.all(bagItems.map((item) => resolveLineStock(item)));
      if (cancelled) return;
      const next = {};
      entries.forEach(({ lineKey, stock }) => {
        next[lineKey] = stock;
      });
      setCartLineStock(next);
    };

    run();
    const intervalId = window.setInterval(run, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isCartOpen, bagItems, resolveLineStock]);

  const getCartLineMaxQty = useCallback(
    (item) => {
      const raw = cartLineStock[item.itemId];
      if (raw == null) return CART_QTY_MAX;
      return purchasableMax(raw);
    },
    [cartLineStock]
  );

  const [contactChannelsTick, setContactChannelsTick] = useState(0);
  useEffect(() => {
    const onChannels = () => setContactChannelsTick((t) => t + 1);
    window.addEventListener(CONTACT_CHANNELS_UPDATED_EVENT, onChannels);
    return () => window.removeEventListener(CONTACT_CHANNELS_UPDATED_EVENT, onChannels);
  }, []);

  const cartDeliveryInfo = useMemo(() => {
    const raw = parseContactChannelsFromStorage();
    const sub = Number(bagTotal) || 0;
    return computeShippingFromSubtotal(sub, raw || {});
  }, [bagTotal, contactChannelsTick]);
  
  // Handle quantity update
  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromBag(itemId);
      return;
    }

    if (isAuthenticated) {
      try {
        setCartLoading(true);
        const response = await cartService.updateCartItem(itemId, newQuantity);
        if (response?.success && response.data) {
          setCart({
            ...response.data,
            items: Array.isArray(response.data.items) ? [...response.data.items] : [],
          });
        } else {
          const refreshed = await cartService.getCart();
          if (refreshed?.data) setCart(refreshed.data);
        }
        window.dispatchEvent(new CustomEvent('cartUpdated'));
      } catch (error) {
        console.error('Error updating cart item:', error);
        if (typeof window.showToast === 'function') {
          window.showToast(error?.message || 'Could not update quantity', 'error', 3500);
        }
      } finally {
        setCartLoading(false);
      }
    } else {
      updateGuestQuantity(itemId, newQuantity);
    }
  };

  const changeCartLineQuantity = (item, delta) => {
    const max = getCartLineMaxQty(item);
    const next = (Number(item.quantity) || 1) + delta;
    if (delta > 0 && next > max) {
      if (typeof window.showToast === 'function') {
        window.showToast(
          max > 0 ? `Only ${max} available in stock` : 'This item is out of stock',
          'error',
          3500
        );
      }
      return;
    }
    updateQuantity(item.itemId, next);
  };

  // Clamp lines when live stock drops below cart quantity
  useEffect(() => {
    if (!Object.keys(cartLineStock).length || !bagItems.length) return;
    bagItems.forEach((item) => {
      const max = purchasableMax(cartLineStock[item.itemId]);
      const qty = Number(item.quantity) || 0;
      if (max > 0 && qty > max) {
        updateQuantity(item.itemId, max);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clamp only when stock map changes
  }, [cartLineStock]);
  
  // Handle remove item
  const removeFromBag = async (itemId) => {
    if (isAuthenticated) {
      try {
        setCartLoading(true);
        await cartService.removeFromCart(itemId);
        // Refresh cart
        const response = await cartService.getCart();
        if (response?.data) {
          setCart(response.data);
        }
      } catch (error) {
        console.error('Error removing cart item:', error);
      } finally {
        setCartLoading(false);
      }
    } else {
      removeGuestItem(itemId);
    }
  };

  // Function to check if current path matches navigation link
  const isActiveLink = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Handle scroll effect (shadow when scrolled)
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // New route: show bar and sync scroll baseline
  useEffect(() => {
    setHeaderBarHiddenMobile(false);
    lastScrollYRef.current = Math.max(0, window.scrollY);
  }, [location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_NAV_MAX_PX}px)`);
    let rafId = null;
    const MIN_SCROLL_DELTA = 10;
    const BOTTOM_LOCK_PX = 24;

    const run = () => {
      if (!mq.matches) {
        setHeaderBarHiddenMobile(false);
        lastScrollYRef.current = Math.max(0, window.scrollY);
        return;
      }
      if (isMenuOpen || isCartOpen || isSearchOpen) {
        setHeaderBarHiddenMobile(false);
        lastScrollYRef.current = Math.max(0, window.scrollY);
        return;
      }

      const y = Math.max(0, window.scrollY);
      const last = lastScrollYRef.current;
      const delta = y - last;
      const doc = document.documentElement;
      const remainingToBottom = Math.max(0, (doc.scrollHeight || 0) - (window.innerHeight + y));
      const nearBottom = remainingToBottom <= BOTTOM_LOCK_PX;

      // Prevent bottom-edge bounce from rapidly toggling header visibility.
      if (nearBottom) {
        lastScrollYRef.current = y;
        return;
      }

      if (y <= 20) {
        setHeaderBarHiddenMobile(false);
      } else if (delta > MIN_SCROLL_DELTA && y > 64) {
        setHeaderBarHiddenMobile(true);
      } else if (delta < -MIN_SCROLL_DELTA) {
        setHeaderBarHiddenMobile(false);
      }

      lastScrollYRef.current = y;
    };

    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        run();
      });
    };

    const onMqChange = () => {
      if (!mq.matches) setHeaderBarHiddenMobile(false);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    mq.addEventListener('change', onMqChange);
    return () => {
      window.removeEventListener('scroll', onScroll);
      mq.removeEventListener('change', onMqChange);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [isMenuOpen, isCartOpen, isSearchOpen]);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_NAV_MAX_PX}px)`);
    if (headerBarHiddenMobile && mq.matches) {
      document.body.classList.add('mobile-header-bar-hidden');
    } else {
      document.body.classList.remove('mobile-header-bar-hidden');
    }
    return () => document.body.classList.remove('mobile-header-bar-hidden');
  }, [headerBarHiddenMobile]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('mobile-nav-drawer-open');
    } else {
      document.body.classList.remove('mobile-nav-drawer-open');
    }
    return () => document.body.classList.remove('mobile-nav-drawer-open');
  }, [isMenuOpen]);

  useEffect(() => {
    if (isCartOpen) {
      document.body.classList.add('mobile-cart-sidebar-open');
    } else {
      document.body.classList.remove('mobile-cart-sidebar-open');
    }
    return () => document.body.classList.remove('mobile-cart-sidebar-open');
  }, [isCartOpen]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    setIsMobileAccountMenuOpen(false);
  }, []);

  /** Lock page scroll while drawer is open (iOS / WebView friendly) */
  useEffect(() => {
    if (!isMenuOpen) return;
    const y = window.scrollY;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.left = prev.left;
      document.body.style.right = prev.right;
      document.body.style.width = prev.width;
      window.scrollTo(0, y);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) setMobileCategoryPanel(null);
  }, [isMenuOpen]);

  // Keep search input synced with search page query.
  useEffect(() => {
    if (location.pathname !== '/search') return;
    const q = new URLSearchParams(location.search).get('q') || '';
    setSearchQuery(q);
  }, [location.pathname, location.search]);

  // Fetch categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await categoryService.getCategories();
        
        let categoriesData = [];
        if (response.success && response.categories) {
          if (Array.isArray(response.categories)) {
            categoriesData = response.categories;
          } else if (Array.isArray(response.categories.data)) {
            categoriesData = response.categories.data;
          }
        } else if (Array.isArray(response)) {
          categoriesData = response;
        }
        
        // Store all categories (including subcategories) for subcategory lookup
        setAllCategories(categoriesData);
        
        // Debug: Log first category structure to understand API response
        if (categoriesData.length > 0) {
          console.log('📋 Header: Sample category structure:', {
            name: categoriesData[0].name,
            _id: categoriesData[0]._id,
            parentCategory: categoriesData[0].parentCategory,
            parent: categoriesData[0].parent
          });
        }
        
        // Filter only main categories (no parentCategory) for menu display
        const mainCategories = categoriesData.filter(cat => {
          const parentId = cat.parentCategory?._id || cat.parentCategory || cat.parent;
          const isMain = !parentId || parentId === '' || parentId === null;
          return isMain;
        });

        const mainCategoriesOrdered = sortMainCategoriesForDisplay(mainCategories);
        
        // Debug: Log subcategories count
        const subcategoriesCount = categoriesData.length - mainCategories.length;
        console.log('✅ Header: Main categories loaded:', mainCategories.length);
        console.log('✅ Header: Subcategories found:', subcategoriesCount);
        console.log('✅ Header: Total categories loaded:', categoriesData.length);
        
        setCategories(mainCategoriesOrdered);
      } catch (error) {
        console.error('❌ Header: Error loading categories:', error);
        setCategories([]);
        setAllCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    
    loadCategories();
  }, []);

  // Helper function to get subcategories for a category
  const getSubcategories = (categoryId) => {
    if (!categoryId || !allCategories.length) {
      console.log('⚠️ Header: No categoryId or allCategories empty', { categoryId, allCategoriesLength: allCategories.length });
      return [];
    }
    
    // Extract the actual ID from categoryId (could be object or string)
    const idToMatch = categoryId._id || categoryId;
    const idString = String(idToMatch).trim();
    
    if (!idString || idString === 'undefined' || idString === 'null') {
      console.log('⚠️ Header: Invalid categoryId:', categoryId);
      return [];
    }
    
    const subcats = allCategories.filter(cat => {
      // Try multiple ways to get parent ID
      let parentId = null;
      
      // Method 1: parentCategory as object with _id
      if (cat.parentCategory) {
        if (typeof cat.parentCategory === 'object' && cat.parentCategory._id) {
          parentId = cat.parentCategory._id;
        } else if (typeof cat.parentCategory === 'object') {
          // If it's an object but no _id, try to use the object itself or find id
          parentId = cat.parentCategory.id || cat.parentCategory;
        } else {
          // Direct string ID
          parentId = cat.parentCategory;
        }
      }
      // Method 2: parent field (fallback)
      else if (cat.parent) {
        if (typeof cat.parent === 'object' && cat.parent._id) {
          parentId = cat.parent._id;
        } else if (typeof cat.parent === 'object') {
          parentId = cat.parent.id || cat.parent;
        } else {
          parentId = cat.parent;
        }
      }
      
      if (!parentId) return false;
      
      // Convert parentId to string for comparison
      const parentIdString = String(parentId).trim();
      
      // Match if IDs are equal
      const matches = parentIdString === idString;
      
      if (matches) {
        console.log('✅ Header: Found subcategory:', cat.name, 'for parent:', idString);
      }
      
      return matches;
    });
    
    console.log(`✅ Header: Found ${subcats.length} subcategories for category "${idString}"`);
    if (subcats.length > 0) {
      console.log('📋 Subcategories:', subcats.map(s => s.name).join(', '));
    }
    
    return subcats;
  };

  // Helper function to get children categories for a subcategory
  const getChildrenCategories = (subcategoryId) => {
    if (!subcategoryId || !allCategories.length) {
      return [];
    }
    
    // Extract the actual ID from subcategoryId (could be object or string)
    const idToMatch = subcategoryId._id || subcategoryId;
    const idString = String(idToMatch).trim();
    
    if (!idString || idString === 'undefined' || idString === 'null') {
      return [];
    }
    
    const children = allCategories.filter(cat => {
      // Try multiple ways to get parent ID
      let parentId = null;
      
      // Method 1: parentCategory as object with _id
      if (cat.parentCategory) {
        if (typeof cat.parentCategory === 'object' && cat.parentCategory._id) {
          parentId = cat.parentCategory._id;
        } else if (typeof cat.parentCategory === 'object') {
          parentId = cat.parentCategory.id || cat.parentCategory;
        } else {
          parentId = cat.parentCategory;
        }
      }
      // Method 2: parent field (fallback)
      else if (cat.parent) {
        if (typeof cat.parent === 'object' && cat.parent._id) {
          parentId = cat.parent._id;
        } else if (typeof cat.parent === 'object') {
          parentId = cat.parent.id || cat.parent;
        } else {
          parentId = cat.parent;
        }
      }
      
      if (!parentId) return false;
      
      // Convert parentId to string for comparison
      const parentIdString = String(parentId).trim();
      
      // Match if IDs are equal
      return parentIdString === idString;
    });
    
    return children;
  };

  // Helper function to create category slug (now returns hierarchical path)
  const getCategorySlug = (category) => {
    return getCategorySlugUtil(category, allCategories);
  };

  const mobileNavDrillThemeKey =
    mobileCategoryPanel == null
      ? 'default'
      : getMobileNavDrillThemeKey(
          mobileCategoryPanel.step === 'mainSubs'
            ? mobileCategoryPanel.main
            : mobileCategoryPanel.focusSub,
          getCategorySlug
        );

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
    }
  };

  const closeSearchPanel = () => setIsSearchOpen(false);

  const toggleSearchPanel = () => {
    setIsUserDropdownOpen(false);
    setIsCartOpen(false);
    setIsMenuOpen(false);
    setIsMobileAccountMenuOpen(false);
    setIsSearchOpen((open) => !open);
  };

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_NAV_MAX_PX}px)`);
    const sync = () => setUseMobileSearchOverlay(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!isSearchOpen || !useMobileSearchOverlay) return;
    const t = window.setTimeout(() => mobileSearchInputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [isSearchOpen, useMobileSearchOverlay]);

  useEffect(() => {
    if (!isSearchOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSearchOpen]);

  const handleUserClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthenticated) {
      setIsUserDropdownOpen((open) => {
        const next = !open;
        if (next) {
          setIsMenuOpen(false);
          setIsCartOpen(false);
          setIsMobileAccountMenuOpen(false);
        }
        return next;
      });
    } else {
      navigate('/login');
    }
  };

  const closeUserDropdown = () => {
    setIsUserDropdownOpen(false);
  };

  const openNotificationsModal = useCallback(() => {
    closeUserDropdown();
    setIsMenuOpen(false);
    setNotificationsModalOpen(true);
  }, []);

  const closeNotificationsModal = useCallback(() => {
    setNotificationsModalOpen(false);
  }, []);

  const handleSignOut = () => {
    closeUserDropdown();
    logout();
  };

  useEffect(() => {
    setIsUserDropdownOpen(false);
  }, [location.pathname]);

  // Close user dropdown on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isUserDropdownOpen) {
        setIsUserDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUserDropdownOpen]);

  const toggleMenu = () => {
    setIsUserDropdownOpen(false);
    setIsSearchOpen(false);
    setIsMenuOpen((prev) => !prev);
  };

  const goBackMobileCategoryPanel = () => {
    setMobileCategoryPanel((panel) => {
      if (!panel) return null;
      if (panel.step === 'subChildren') {
        return { step: 'mainSubs', main: panel.main };
      }
      return null;
    });
  };

  const openMobileCategoryOrNavigate = (category) => {
    const categoryId = category._id || category.id;
    const subs = getSubcategories(categoryId);
    if (subs.length > 0) {
      setMobileCategoryPanel({ step: 'mainSubs', main: category });
      return;
    }
    const slug = getCategorySlug(category);
    navigate(`/category/${slug}`);
    closeMenu();
  };

  const onMobileSubcategoryTap = (main, sub) => {
    const subId = sub._id || sub.id;
    const children = getChildrenCategories(subId);
    if (children.length > 0) {
      setMobileCategoryPanel({ step: 'subChildren', main, focusSub: sub });
      return;
    }
    const slug = getCategorySlug(sub);
    navigate(`/category/${slug}`);
    closeMenu();
  };

  const onMobileChildCategoryTap = (main, focusSub, child) => {
    const childId = child._id || child.id;
    const nested = getChildrenCategories(childId);
    if (nested.length > 0) {
      setMobileCategoryPanel({ step: 'subChildren', main, focusSub: child });
      return;
    }
    const slug = getCategorySlug(child);
    navigate(`/category/${slug}`);
    closeMenu();
  };

  const toggleCart = () => {
    setIsUserDropdownOpen(false);
    setIsSearchOpen(false);
    setIsCartOpen((open) => !open);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const handleMobileProfileNav = (section = null) => {
    closeMenu();
    if (section) {
      navigate('/profile', { state: { section } });
      return;
    }
    navigate('/profile');
  };

  const handleMobileMenuSignOut = () => {
    closeMenu();
    logout();
  };

  // Cart item removal confirmation functions
  const handleRemoveItem = (itemId) => {
    setItemToRemove(itemId);
    setShowConfirmation(true);
  };

  const confirmRemoveItem = () => {
    if (itemToRemove) {
      removeFromBag(itemToRemove);
      setShowConfirmation(false);
      setItemToRemove(null);
    }
  };

  const cancelRemoveItem = () => {
    setShowConfirmation(false);
    setItemToRemove(null);
  };

  const renderCartShippingPromo = () => {
    const d = cartDeliveryInfo;
    const sub = Number(bagTotal) || 0;
    const th = Number(d.threshold) || 0;
    const pct =
      !d.freeDeliveryEnabled || th <= 0
        ? d.qualified
          ? 100
          : 0
        : Math.min(100, Math.round((sub / th) * 100));

    if (!d.freeDeliveryEnabled) {
      return (
        <div className="cart-shipping-promo cart-shipping-promo--standard" role="status">
          <div className="cart-shipping-promo__lead">
            <span className="cart-shipping-promo__emoji" aria-hidden>
              🚚
            </span>
            <div className="cart-shipping-promo__text">
              <span className="cart-shipping-promo__title">Delivery</span>
              <span className="cart-shipping-promo__desc">
                Standard shipping PKR {d.standardShippingFee.toLocaleString()} on this order (free
                delivery promos are off).
              </span>
            </div>
          </div>
          <div className="cart-shipping-promo__subtotal">
            <span>Bag subtotal</span>
            <strong>PKR {sub.toLocaleString()}</strong>
          </div>
        </div>
      );
    }

    if (d.qualified) {
      return (
        <div className="cart-shipping-promo cart-shipping-promo--qualified" role="status">
          <div className="cart-shipping-promo__lead">
            <span className="cart-shipping-promo__emoji cart-shipping-promo__emoji--ok" aria-hidden>
              ✓
            </span>
            <div className="cart-shipping-promo__text">
              <span className="cart-shipping-promo__title">Free delivery unlocked</span>
              <span className="cart-shipping-promo__desc">
                Your bag is PKR {th.toLocaleString()}+ — no shipping fee on this order.
              </span>
            </div>
          </div>
          <div className="cart-shipping-promo__track" aria-hidden>
            <div className="cart-shipping-promo__fill" style={{ width: '100%' }} />
          </div>
          <div className="cart-shipping-promo__labels">
            <span>PKR 0</span>
            <span>PKR {th.toLocaleString()}</span>
          </div>
          <div className="cart-shipping-promo__subtotal">
            <span>Bag subtotal</span>
            <strong>PKR {sub.toLocaleString()}</strong>
          </div>
        </div>
      );
    }

    const remaining = d.amountToAdd != null ? d.amountToAdd : Math.max(0, th - sub);
    return (
      <div className="cart-shipping-promo cart-shipping-promo--progress" role="status">
        <div className="cart-shipping-promo__lead">
          <span className="cart-shipping-promo__emoji" aria-hidden>
            🎁
          </span>
          <div className="cart-shipping-promo__text">
            <span className="cart-shipping-promo__title">Free delivery</span>
            <span className="cart-shipping-promo__desc">
              Add <strong>PKR {remaining.toLocaleString()}</strong> more to unlock free shipping (orders
              PKR {th.toLocaleString()}+).
            </span>
          </div>
        </div>
        <div
          className="cart-shipping-promo__track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label="Progress toward free delivery"
        >
          <div className="cart-shipping-promo__fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="cart-shipping-promo__labels">
          <span>PKR 0</span>
          <span>
            PKR {th.toLocaleString()} goal · {pct}% there
          </span>
        </div>
        <div className="cart-shipping-promo__hint">
          Bag <strong>PKR {sub.toLocaleString()}</strong>
          <span className="cart-shipping-promo__hint-sep"> · </span>
          or pay PKR {d.standardShippingFee.toLocaleString()} shipping
        </div>
      </div>
    );
  };

  return (
    <header
      className={`header ${isScrolled ? 'scrolled' : ''}${
        headerBarHiddenMobile ? ' header--mobile-bar-collapsed' : ''
      }${isUserDropdownOpen ? ' header--account-menu-open' : ''}`}
    >
      {/* Main header */}
      <div
        className={`header-main${headerBarHiddenMobile ? ' header-main--scroll-hidden-mobile' : ''}`}
      >
        <div className="header-content">
            {/* MOBILE HEADER ROW (custom order) */}
            <div className="mobile-header-row">
              <div className="mobile-header-cluster mobile-header-cluster--left">
                {/* Far left: Hamburger menu */}
                <div className="mobile-header-col mobile-header-menu">
                  <button 
                    className="mobile-menu-btn"
                    onClick={toggleMenu}
                    aria-label="Toggle menu"
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                  </button>
                </div>
                {/* Search */}
                <div className="mobile-header-col mobile-header-search-group">
                  <button
                    type="button"
                    className="mobile-search-btn"
                    onClick={toggleSearchPanel}
                    aria-label="Search"
                    aria-expanded={isSearchOpen}
                  >
                    🔍
                  </button>
                </div>
              </div>
              {/* Center: Logo (CSS: absolute center of row — reliable in WebView) */}
              <div className="mobile-header-col mobile-header-logo">
                {renderSiteBrandLink(
                  `logo mobile-center-logo ${isActiveLink('/') ? 'active' : ''}`,
                  { onNavigate: () => setIsMenuOpen(false) }
                )}
              </div>
              <div className="mobile-header-cluster mobile-header-cluster--right">
              {/* User avatar */}
              <div className="mobile-header-col mobile-header-user">
                <div className="account-container">
                  <button 
                    type="button"
                    className={`mobile-account-btn${isAuthenticated ? ' mobile-account-btn--logged-in' : ''}`}
                    onClick={handleUserClick}
                    aria-label="Account"
                    aria-expanded={isAuthenticated ? isUserDropdownOpen : undefined}
                    aria-haspopup={isAuthenticated ? 'menu' : undefined}
                  >
                    {isAuthenticated ? (
                      <div className="user-avatar">
                        {user.customAvatar ? (
                          <img src={user.customAvatar} alt={user.name} />
                        ) : (
                          <div className="avatar-letter">
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="account-icon account-icon--mobile-gold">
                        <MobileGuestUserIcon />
                      </div>
                    )}
                  </button>

                  {/* Mobile User Dropdown Menu */}
                  {isAuthenticated && isUserDropdownOpen && (
                    <div className="user-dropdown mobile-user-dropdown">
                      <div className="dropdown-header">
                        <div className="user-info">
                          {user.customAvatar ? (
                            <img src={user.customAvatar} alt={user.name} className="dropdown-avatar" />
                          ) : (
                            <div className="dropdown-avatar-letter">
                              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                            </div>
                          )}
                          <div className="user-details">
                            <div className="user-name">{user.name || 'User'}</div>
                            <div className="user-email">{user.email || ''}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="dropdown-divider"></div>
                      
                      <div className="dropdown-options" role="menu">
                        <Link
                          to="/profile"
                          className="dropdown-option"
                          role="menuitem"
                          onClick={closeUserDropdown}
                        >
                          <span className="option-icon" aria-hidden>👤</span>
                          <span>My Account</span>
                        </Link>

                        <Link
                          to="/orders"
                          className="dropdown-option"
                          role="menuitem"
                          onClick={closeUserDropdown}
                        >
                          <span className="option-icon" aria-hidden>📋</span>
                          <span>My Orders</span>
                        </Link>

                        <button
                          type="button"
                          className="dropdown-option"
                          role="menuitem"
                          onClick={openNotificationsModal}
                        >
                          <span className="option-icon" aria-hidden>🔔</span>
                          <span>Notifications</span>
                          {unreadCount > 0 ? (
                            <span className="header-notif-badge">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          ) : null}
                        </button>

                        <div className="dropdown-divider" />

                        <button
                          type="button"
                          className="dropdown-option logout-option"
                          role="menuitem"
                          onClick={handleSignOut}
                        >
                          <span className="option-icon" aria-hidden>🚪</span>
                          <span>Sign out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Far right: Bag icon */}
              <div className="mobile-header-col mobile-header-bag">
                <button 
                  className="mobile-bag-btn"
                  onClick={toggleCart}
                  aria-label="Shopping Bag"
                >
                  🛍️
                  {bagCount > 0 && <span className="bag-count">{bagCount}</span>}
                </button>
              </div>
              </div>
            </div>
            {/* DESKTOP HEADER (unchanged) */}
            <div className="desktop-header-content">
              {/* Center section - Logo & Navigation */}
              <div className="header-center">
                {/* Logo */}
                {renderSiteBrandLink(`logo ${isActiveLink('/') ? 'active' : ''}`)}
                {/* Desktop Navigation */}
                <nav className="desktop-nav">
              {/* New In - Shows New Arrival products */}
              <div className="nav-item">
                <Link to="/category/new-in" className={isActiveLink('/category/new-in') ? 'active' : ''}>New In</Link>
              </div>

              {/* Dynamic Categories from API */}
              {categories.map((category) => {
                const categorySlug = getCategorySlug(category);
                const categoryId = category._id || category.id;
                const subcategories = getSubcategories(categoryId);
                const hasSubcategories = subcategories.length > 0;
                
                // Skip if this is a special category (we'll handle it separately)
                if (category.name.toLowerCase() === 'special' || category.name.toLowerCase() === 'on sale') {
                  return null;
                }
                
                return (
                  <div key={categoryId} className={`nav-item ${hasSubcategories ? 'dropdown' : ''}`}>
                    <Link 
                      to={`/category/${categorySlug}`} 
                      className={isActiveLink(`/category/${categorySlug}`) ? 'active' : ''}
                      onClick={(e) => {
                        if (hasSubcategories && window.innerWidth <= MOBILE_NAV_MAX_PX) {
                          const isExpanded = expandedSubcategories.has(categoryId);
                          if (!isExpanded) {
                            // First click: open submenu, prevent navigation
                            e.preventDefault();
                            const newExpanded = new Set(expandedSubcategories);
                            newExpanded.add(categoryId);
                            setExpandedSubcategories(newExpanded);
                          }
                          // Second click: allow navigation (don't prevent default)
                        }
                      }}
                    >
                      {category.name}
                      {hasSubcategories && <span className={`dropdown-arrow ${expandedSubcategories.has(categoryId) ? 'expanded' : ''}`}>▼</span>}
                    </Link>
                    {hasSubcategories && (
                      <div className={`dropdown-content ${expandedSubcategories.has(categoryId) ? 'mobile-expanded' : ''}`}>
                        {subcategories.map((subcat) => {
                          const subcatSlug = getCategorySlug(subcat); // Now returns hierarchical path
                          const subcatId = subcat._id || subcat.id;
                          const childrenCategories = getChildrenCategories(subcatId);
                          const hasChildren = childrenCategories.length > 0;
                          const isChildrenExpanded = expandedChildren.has(subcatId);
                          
                          return (
                            <div key={subcatId} className="dropdown-item-wrapper">
                              <Link 
                                to={`/category/${subcatSlug}`}
                                className="dropdown-link"
                                onClick={(e) => {
                                  if (hasChildren && window.innerWidth <= MOBILE_NAV_MAX_PX) {
                                    const isExpanded = expandedChildren.has(subcatId);
                                    if (!isExpanded) {
                                      // First click: open children menu, prevent navigation
                                      e.preventDefault();
                                      const newExpanded = new Set(expandedChildren);
                                      newExpanded.add(subcatId);
                                      setExpandedChildren(newExpanded);
                                    }
                                    // Second click: allow navigation (don't prevent default)
                                  }
                                }}
                              >
                                {subcat.name}
                                {hasChildren && <span className={`children-indicator ${isChildrenExpanded ? 'expanded' : ''}`}>▶</span>}
                              </Link>
                              {hasChildren && (
                                <div className={`children-dropdown ${isChildrenExpanded ? 'mobile-expanded' : ''}`}>
                                  {childrenCategories.map((childCat) => {
                                    const childSlug = getCategorySlug(childCat); // Now returns hierarchical path
                                    const childId = childCat._id || childCat.id;
                                    return (
                                      <Link 
                                        key={childId} 
                                        to={`/category/${childSlug}`}
                                        className="children-link"
                                      >
                                        {childCat.name}
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* On Sale - Shows On Sale products (renamed from Special) */}
              <div className="nav-item">
                <Link to="/category/on-sale" className={isActiveLink('/category/on-sale') ? 'active' : ''}>On Sale</Link>
              </div>
              </nav>
              </div>
              {/* Header actions (search, user, bag) */}
              <div className="header-actions">
                {/* Search */}
                <div className={`search-container ${isSearchOpen ? 'open' : ''}`}>
                  <button 
                    className="search-btn"
                    onClick={() => setIsSearchOpen(!isSearchOpen)}
                    aria-label="Search"
                  >
                    🔍
                  </button>
                  <form onSubmit={handleSearch} className="search-form">
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                    <button type="submit" className="search-submit">
                      Search
                    </button>
                  </form>
                </div>

                {/* User Account */}
                <div className="account-container">
                  <button 
                    type="button"
                    className="account-btn"
                    onClick={handleUserClick}
                    aria-label="Account"
                    aria-expanded={isAuthenticated ? isUserDropdownOpen : undefined}
                    aria-haspopup={isAuthenticated ? 'menu' : undefined}
                  >
                    {isAuthenticated ? (
                      <div className="user-avatar">
                        {user.customAvatar ? (
                          <img src={user.customAvatar} alt={user.name} />
                        ) : (
                          <div className="avatar-letter">
                            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="account-icon account-icon--mobile-gold">
                        <MobileGuestUserIcon />
                      </div>
                    )}
                  </button>

                  {/* User Dropdown Menu */}
                  {isAuthenticated && isUserDropdownOpen && (
                    <div className="user-dropdown">
                      <div className="dropdown-header">
                        <div className="user-info">
                          {user.customAvatar ? (
                            <img src={user.customAvatar} alt={user.name} className="dropdown-avatar" />
                          ) : (
                            <div className="dropdown-avatar-letter">
                              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                            </div>
                          )}
                          <div className="user-details">
                            <div className="user-name">{user.name || 'User'}</div>
                            <div className="user-email">{user.email || ''}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="dropdown-divider"></div>
                      
                      <div className="dropdown-options" role="menu">
                        <Link
                          to="/profile"
                          className="dropdown-option"
                          role="menuitem"
                          onClick={closeUserDropdown}
                        >
                          <span className="option-icon" aria-hidden>👤</span>
                          <span>My Account</span>
                        </Link>

                        <Link
                          to="/orders"
                          className="dropdown-option"
                          role="menuitem"
                          onClick={closeUserDropdown}
                        >
                          <span className="option-icon" aria-hidden>📋</span>
                          <span>My Orders</span>
                        </Link>

                        <button
                          type="button"
                          className="dropdown-option"
                          role="menuitem"
                          onClick={openNotificationsModal}
                        >
                          <span className="option-icon" aria-hidden>🔔</span>
                          <span>Notifications</span>
                          {unreadCount > 0 ? (
                            <span className="header-notif-badge">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          ) : null}
                        </button>

                        <div className="dropdown-divider" />

                        <button
                          type="button"
                          className="dropdown-option logout-option"
                          role="menuitem"
                          onClick={handleSignOut}
                        >
                          <span className="option-icon" aria-hidden>🚪</span>
                          <span>Sign out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Shopping Bag */}
                <button 
                  className="bag-btn"
                  onClick={toggleCart}
                  aria-label="Shopping Bag"
                >
                  🛍️
                  {bagCount > 0 && <span className="bag-count">{bagCount}</span>}
                </button>
              </div>
            </div>
        </div>
      </div>

      {/* Mobile / tablet search (desktop uses .search-container inside .desktop-header-content) */}
      {isSearchOpen && useMobileSearchOverlay && (
        <div
          className="mobile-search-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Search products"
        >
          <button
            type="button"
            className="mobile-search-overlay__backdrop"
            aria-label="Close search"
            onClick={closeSearchPanel}
          />
          <div className="mobile-search-overlay__panel">
            <div className="mobile-search-overlay__head">
              <h2 className="mobile-search-overlay__title">Search</h2>
              <button
                type="button"
                className="mobile-search-overlay__close"
                onClick={closeSearchPanel}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <form className="mobile-search-overlay__form" onSubmit={handleSearch}>
              <input
                ref={mobileSearchInputRef}
                type="search"
                name="q"
                className="mobile-search-overlay__input"
                placeholder="Search products, categories…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
                enterKeyHint="search"
              />
              <button type="submit" className="mobile-search-overlay__submit">
                Go
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Navigation */}
      <div
        className={[
          'mobile-nav',
          isMenuOpen ? 'open' : '',
          mobileCategoryPanel ? 'mobile-nav--drill' : '',
          mobileCategoryPanel ? `mobile-nav--theme-${mobileNavDrillThemeKey}` : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="mobile-nav-content">
          <div className="mobile-nav-header">
            {mobileCategoryPanel ? (
              <>
                <button
                  type="button"
                  className="mobile-nav-back-btn"
                  onClick={goBackMobileCategoryPanel}
                  aria-label="Back"
                >
                  ←
                </button>
                <h3 className="mobile-nav-header-drill-title">
                  {mobileCategoryPanel.step === 'mainSubs'
                    ? mobileCategoryPanel.main?.name
                    : mobileCategoryPanel.focusSub?.name}
                </h3>
                <button type="button" onClick={closeMenu} className="close-btn" aria-label="Close menu">
                  ✕
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={closeMenu}
                  className="close-btn mobile-nav-close-menu"
                  aria-label="Close menu"
                >
                  ✕
                </button>
                <h3>Menu</h3>
                <ThemeModeIconButton className="mobile-nav-theme-toggle" />
              </>
            )}
          </div>
          
          <div className="mobile-nav-links">
            {/* Collapsible My Account */}
            <div className="mobile-nav-account">
              <button
                type="button"
                className={`mobile-nav-account-toggle${isMobileAccountMenuOpen ? ' is-open' : ''}`}
                onClick={() => setIsMobileAccountMenuOpen((v) => !v)}
                aria-expanded={isMobileAccountMenuOpen}
                aria-controls="mobile-account-menu"
                aria-label={
                  isMobileAccountMenuOpen
                    ? 'Collapse My Account menu'
                    : 'Expand My Account menu'
                }
              >
                <span className="mobile-nav-account-toggle-label">👤 My Account</span>
                <span className="mobile-nav-account-toggle-meta" aria-hidden>
                  <span className="mobile-nav-account-toggle-hint">
                    {isMobileAccountMenuOpen ? 'Collapse' : 'Expand'}
                  </span>
                  <span className="mobile-nav-account-toggle-chevron" />
                </span>
              </button>
              <div
                id="mobile-account-menu"
                className={`mobile-nav-account-content ${isMobileAccountMenuOpen ? 'open' : ''}`}
                role="region"
                aria-label="My Account links"
              >
                {isAuthenticated ? (
                  <>
                    <button type="button" className="mobile-nav-link-btn" onClick={() => handleMobileProfileNav('profile')}>
                      👤 Profile
                    </button>
                    <button type="button" className="mobile-nav-link-btn" onClick={() => handleMobileProfileNav('addresses')}>
                      📍 Shipping Address Book
                    </button>
                    <Link to="/orders" onClick={closeMenu} className={isActiveLink('/orders') ? 'active' : ''}>
                      📋 Order History
                    </Link>
                    <button
                      type="button"
                      className="mobile-nav-link-btn mobile-nav-link-btn--inline"
                      onClick={() => {
                        closeMenu();
                        setNotificationsModalOpen(true);
                      }}
                    >
                      🔔 Recent notifications
                      {unreadCount > 0 ? (
                        <span className="mobile-nav-notif-badge">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      ) : null}
                    </button>
                    <button type="button" className="mobile-nav-link-btn" onClick={() => handleMobileProfileNav('account')}>
                      👤 Account
                    </button>
                    <button type="button" className="mobile-nav-link-btn" onClick={() => handleMobileProfileNav('security')}>
                      🔒 Security
                    </button>
                    <button type="button" className="mobile-nav-link-btn" onClick={() => handleMobileProfileNav('notifications')}>
                      🔔 Notifications
                    </button>
                    <button type="button" className="mobile-nav-link-btn" onClick={() => handleMobileProfileNav('coupons')}>
                      🎫 Coupons
                    </button>
                    <button type="button" className="mobile-nav-link-btn" onClick={() => handleMobileProfileNav('support')}>
                      🆘 Support
                    </button>
                    <button type="button" className="mobile-nav-link-btn mobile-nav-link-btn--signout" onClick={handleMobileMenuSignOut}>
                      🚪 Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={closeMenu} className={isActiveLink('/login') ? 'active' : ''}>
                      🔐 Sign In
                    </Link>
                    <Link to="/signup" onClick={closeMenu} className={isActiveLink('/signup') ? 'active' : ''}>
                      ✨ Create account
                    </Link>
                  </>
                )}
              </div>
            </div>
            {!mobileCategoryPanel ? (
              <>
                <Link
                  to="/category/new-in"
                  onClick={closeMenu}
                  className={isActiveLink('/category/new-in') ? 'active' : ''}
                >
                  New In
                </Link>
                {categoriesLoading ? (
                  <div className="mobile-nav-cat-loading">Loading categories…</div>
                ) : (
                  categories.map((category) => {
                    if (
                      category.name.toLowerCase() === 'special' ||
                      category.name.toLowerCase() === 'on sale'
                    ) {
                      return null;
                    }
                    const categoryId = category._id || category.id;
                    const subs = getSubcategories(categoryId);
                    const hasSubs = subs.length > 0;
                    return (
                      <button
                        key={categoryId}
                        type="button"
                        className={`mobile-nav-cat-row${isActiveLink(`/category/${getCategorySlug(category)}`) ? ' mobile-nav-cat-row--active' : ''}`}
                        onClick={() => openMobileCategoryOrNavigate(category)}
                      >
                        <span>{category.name}</span>
                        {hasSubs ? <span className="mobile-nav-cat-chevron" aria-hidden>›</span> : null}
                      </button>
                    );
                  })
                )}
                <Link
                  to="/category/on-sale"
                  onClick={closeMenu}
                  className={isActiveLink('/category/on-sale') ? 'active' : ''}
                >
                  On Sale
                </Link>
              </>
            ) : mobileCategoryPanel.step === 'mainSubs' ? (
              <div className="mobile-nav-cat-panel mobile-nav-cat-panel--drill" role="navigation" aria-label="Subcategories">
                <Link
                  to={`/category/${getCategorySlug(mobileCategoryPanel.main)}`}
                  onClick={closeMenu}
                  className={`mobile-nav-cat-parent-link${isActiveLink(`/category/${getCategorySlug(mobileCategoryPanel.main)}`) ? ' mobile-nav-cat-parent-link--active' : ''}`}
                >
                  <span className="mobile-nav-cat-parent-link__label">Shop all</span>
                  <span className="mobile-nav-cat-parent-link__name">{mobileCategoryPanel.main.name}</span>
                </Link>
                {getSubcategories(mobileCategoryPanel.main._id || mobileCategoryPanel.main.id).map(
                  (sub) => {
                    const subId = sub._id || sub.id;
                    const hasKids = getChildrenCategories(subId).length > 0;
                    return (
                      <button
                        key={subId}
                        type="button"
                        className="mobile-nav-cat-row"
                        onClick={() => onMobileSubcategoryTap(mobileCategoryPanel.main, sub)}
                      >
                        <span>{sub.name}</span>
                        {hasKids ? <span className="mobile-nav-cat-chevron" aria-hidden>›</span> : null}
                      </button>
                    );
                  }
                )}
              </div>
            ) : (
              <div className="mobile-nav-cat-panel mobile-nav-cat-panel--split mobile-nav-cat-panel--drill" role="navigation">
                <Link
                  to={`/category/${getCategorySlug(mobileCategoryPanel.focusSub)}`}
                  onClick={closeMenu}
                  className={`mobile-nav-cat-parent-link${isActiveLink(`/category/${getCategorySlug(mobileCategoryPanel.focusSub)}`) ? ' mobile-nav-cat-parent-link--active' : ''}`}
                >
                  <span className="mobile-nav-cat-parent-link__label">Shop all</span>
                  <span className="mobile-nav-cat-parent-link__name">{mobileCategoryPanel.focusSub.name}</span>
                </Link>
                <div className="mobile-nav-cat-section">
                  <div className="mobile-nav-cat-section-title">Subcategories</div>
                  {getSubcategories(
                    mobileCategoryPanel.main._id || mobileCategoryPanel.main.id
                  ).map((sub) => {
                    const subId = sub._id || sub.id;
                    const focusId =
                      mobileCategoryPanel.focusSub._id || mobileCategoryPanel.focusSub.id;
                    const isActive = String(subId) === String(focusId);
                    const hasKids = getChildrenCategories(subId).length > 0;
                    return (
                      <button
                        key={subId}
                        type="button"
                        className={`mobile-nav-cat-row${isActive ? ' mobile-nav-cat-row--current' : ''}`}
                        onClick={() => onMobileSubcategoryTap(mobileCategoryPanel.main, sub)}
                      >
                        <span>{sub.name}</span>
                        {hasKids ? <span className="mobile-nav-cat-chevron" aria-hidden>›</span> : null}
                      </button>
                    );
                  })}
                </div>
                {getChildrenCategories(
                  mobileCategoryPanel.focusSub._id || mobileCategoryPanel.focusSub.id
                ).length > 0 ? (
                  <div className="mobile-nav-cat-section">
                    <div className="mobile-nav-cat-section-title">More categories</div>
                    {getChildrenCategories(
                      mobileCategoryPanel.focusSub._id || mobileCategoryPanel.focusSub.id
                    ).map((child) => {
                      const childId = child._id || child.id;
                      const hasNest = getChildrenCategories(childId).length > 0;
                      return (
                        <button
                          key={childId}
                          type="button"
                          className="mobile-nav-cat-row"
                          onClick={() =>
                            onMobileChildCategoryTap(
                              mobileCategoryPanel.main,
                              mobileCategoryPanel.focusSub,
                              child
                            )
                          }
                        >
                          <span>{child.name}</span>
                          {hasNest ? <span className="mobile-nav-cat-chevron" aria-hidden>›</span> : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}

            <div className="mobile-nav-trust" role="group" aria-label="About and policies">
              <span className="mobile-nav-trust-label">About &amp; policies</span>
              <Link to="/about" onClick={closeMenu} className={isActiveLink('/about') ? 'active' : ''}>
                About Us
              </Link>
              <Link to="/contact" onClick={closeMenu} className={isActiveLink('/contact') ? 'active' : ''}>
                Contact
              </Link>
              <Link to="/return-policy" onClick={closeMenu} className={isActiveLink('/return-policy') ? 'active' : ''}>
                Return Policy
              </Link>
              <Link to="/privacy-policy" onClick={closeMenu} className={isActiveLink('/privacy-policy') ? 'active' : ''}>
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Cart Sidebar */}
      <div className={`cart-sidebar ${isCartOpen ? 'open' : ''}`}>
        <div className="cart-sidebar-content">
          <div className="cart-sidebar-header">
            <h3>SHOPPING CART</h3>
            <button onClick={closeCart} className="close-btn">✕</button>
          </div>
          
          {bagCount === 0 ? (
            <div className="empty-cart">
              <p>Your cart is empty</p>
            </div>
          ) : (
            <>
              {renderCartShippingPromo()}

              <div className="cart-items">
                {bagItems.map((item) => (
                  <div key={item.itemId} className="cart-item">
                    <div className="cart-item-image">
                      <img src={item.image} alt={item.name} />
                    </div>
                    <div className="cart-item-details">
                      <h4>{item.name}</h4>
                      <div className="item-size-color">
                        {item.selectedSize && item.selectedSize !== 'default' && (
                          <span className="item-size">Size: {item.selectedSize}</span>
                        )}
                        {item.selectedColor && item.selectedColor !== 'default' && (
                          <span className="item-color">Color: {item.selectedColor}</span>
                        )}
                      </div>
                      <div className="item-price-container">
                        {item.originalPrice && item.originalPrice > item.price ? (
                          <>
                            <span className="item-price-sale">Rs.{item.price.toLocaleString()}</span>
                            <span className="item-price-original">Rs.{item.originalPrice.toLocaleString()}</span>
                          </>
                        ) : (
                          <span className="item-price">Rs.{item.price.toLocaleString()}</span>
                        )}
                      </div>
                      
                      <div className="item-controls">
                        <div className="quantity-controls">
                          <button 
                            onClick={() => changeCartLineQuantity(item, -1)}
                            className="quantity-btn"
                            disabled={item.quantity <= 1 || cartLoading}
                          >
                            -
                          </button>
                          <span className="quantity">{item.quantity}</span>
                          <button 
                            onClick={() => changeCartLineQuantity(item, 1)}
                            className="quantity-btn"
                            disabled={
                              cartLoading ||
                              item.quantity >= getCartLineMaxQty(item) ||
                              getCartLineMaxQty(item) === 0
                            }
                          >
                            +
                          </button>
                        </div>
                        
                        <button 
                          className="delete-btn cart-sidebar-delete-btn" 
                          onClick={() => handleRemoveItem(item.itemId)}
                          title="Remove from cart"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-footer">
                <div className="cart-subtotal">
                  <strong>Subtotal: Rs.{bagTotal.toLocaleString()}</strong>
                </div>
                
                <div className="cart-actions">
                  <button 
                    className="cart-action-btn primary"
                    onClick={() => {
                      navigate('/bag');
                      closeCart();
                    }}
                  >
                    VIEW CART
                  </button>
                  <button 
                    className="cart-action-btn primary"
                    onClick={async () => {
                      try {
                        if (isAuthenticated) {
                          await mergeGuestCartToApi(clearGuestBag, () => guestBagReactSnapshotRef.current);
                        }
                      } catch {
                        /* checkout page will retry merge */
                      }
                      navigate('/checkout');
                      closeCart();
                    }}
                  >
                    CHECKOUT
                  </button>
                  <button 
                    type="button"
                    className="cart-action-btn secondary"
                    onClick={() => {
                      navigate('/');
                      closeCart();
                    }}
                  >
                    CONTINUE SHOPPING
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Overlays */}
      {isCartOpen && <div className="overlay overlay--cart-backdrop" onClick={closeCart}></div>}
      {isUserDropdownOpen && (
        <div className="overlay overlay--user-dropdown" onClick={closeUserDropdown}></div>
      )}

      {/* Cart Item Removal Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={cancelRemoveItem}
        onConfirm={confirmRemoveItem}
        title="Remove Item"
        message="Are you sure you want to remove this item from your cart?"
      />

      <UserNotificationsModal
        isOpen={notificationsModalOpen}
        onClose={closeNotificationsModal}
        items={recentNotifications}
      />

      {isMenuOpen &&
        useMobileSearchOverlay &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="mobile-nav-dismiss-scrim"
            role="button"
            tabIndex={-1}
            aria-label="Close menu"
            onClick={(e) => {
              e.stopPropagation();
              closeMenu();
            }}
            onPointerDown={(e) => {
              if (e.pointerType === 'mouse' && e.button !== 0) return;
              e.stopPropagation();
              closeMenu();
            }}
          />,
          document.body
        )}
    </header>
  );
};

export default Header; 